import { useState, useRef } from "react";
import { Search, MapPin, Book, Map, Lightbulb, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";

const getSuggestions = (t: (key: string) => string) => [
  { type: "guide", title: t("hero.suggestions.london"), subtitle: t("hero.suggestions.england"), icon: Book, href: "/product/london-guide" },
  { type: "guide", title: t("hero.suggestions.paris"), subtitle: t("hero.suggestions.france"), icon: Book, href: "/product/paris-guide" },
  { type: "map", title: t("hero.suggestions.barcelona"), subtitle: t("hero.suggestions.spain"), icon: Map, href: "/product/barcelona-map" },
  { type: "tip", title: t("hero.suggestions.thailand"), subtitle: t("hero.suggestions.asia"), icon: Lightbulb, href: "/product/thailand-tips" },
  { type: "guide", title: t("hero.suggestions.rome"), subtitle: t("hero.suggestions.italy"), icon: Book, href: "/product/rome-guide" },
];

// LOCAL hero images - eliminates DNS/SSL handshake delays for LCP
const HERO_POSTER = {
  mobile: "/hero-mobile.avif",
  desktop: "/hero-optimized.avif",
};

export const HeroSection = () => {
  const { t } = useTranslation();
  const { isRTL } = useLocale();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = getSuggestions(t);

  const filteredSuggestions = searchQuery.length > 0
    ? suggestions.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : suggestions;

  const showDropdown = isFocused && filteredSuggestions.length > 0;

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "guide": return t("hero.guide");
      case "map": return t("hero.map");
      case "tip": return t("hero.tips");
      default: return type;
    }
  };

  return (
    <section 
      className="relative min-h-[100svh] h-screen flex items-center justify-center overflow-visible pt-20 md:pt-24"
      aria-label={t("nav.home")}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* STATIC Hero Background - NO video, NO animations for maximum LCP */}
      <div className="absolute inset-0" aria-hidden="true">
        {/* Static local hero image - instant visibility, no transitions */}
        <img
          src={HERO_POSTER.desktop}
          srcSet={`${HERO_POSTER.mobile} 800w, ${HERO_POSTER.desktop} 1920w`}
          sizes="100vw"
          alt=""
          width={1920}
          height={1080}
          loading="eager"
          // @ts-ignore - fetchpriority is valid HTML but not yet in React types
          fetchpriority="high"
          decoding="sync"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 1 }} // Force instant visibility
        />
        <div className="absolute inset-0 bg-foreground/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      {/* Content - NO ANIMATIONS on LCP elements for instant visibility */}
      <div className="container relative z-10 text-center px-4">
        <div>
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 text-white"
            style={{ textShadow: "0 4px 20px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)" }}
          >
            <span>{t("hero.title_1")} </span>
            <span className="text-secondary">{t("hero.title_2")}</span>
            <span className="sr-only"> - Travel Boost Travel Tools & Trip Planner</span>
          </h1>
          <p 
            className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-12"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.4)" }}
          >
            {t("hero.subtitle")}
          </p>
        </div>

        {/* Large Centered Search Bar - no animation delay */}
        <div className="relative max-w-2xl mx-auto">
          <div 
            className={cn(
              "relative bg-background rounded-2xl shadow-2xl transition-all duration-300",
              isFocused ? "ring-2 ring-primary shadow-primary/20" : ""
            )}
          >
            <div className="relative flex items-center">
              <Search className={cn("absolute h-6 w-6 text-muted-foreground", isRTL ? "right-5" : "left-5")} />
              <Input
                ref={inputRef}
                type="text"
                placeholder={t("hero.search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                className={cn(
                  "w-full h-16 md:h-18 text-lg md:text-xl border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground",
                  isRTL ? "pr-14 pl-14" : "pl-14 pr-14"
                )}
              />
              <ChevronDown className={cn(
                "absolute h-5 w-5 text-muted-foreground transition-transform duration-300",
                isRTL ? "left-5" : "right-5",
                isFocused ? "rotate-180" : ""
              )} />
            </div>
          </div>

          {/* Live Search Dropdown */}
          <div
            className={cn(
              "absolute top-full left-0 right-0 mt-2 bg-background rounded-2xl shadow-2xl border border-border transition-all duration-300 z-[100]",
              showDropdown
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-4 pointer-events-none"
            )}
          >
            <div className="p-2">
              <p className="text-xs text-muted-foreground px-3 py-2">
                {searchQuery ? t("hero.search_results") : t("hero.popular_destinations")}
              </p>
              <div className="space-y-1 pb-20 max-h-[50vh] overflow-y-auto">
                {filteredSuggestions.map((suggestion, index) => (
                  <Link
                    key={index}
                    to={suggestion.href}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl hover:bg-accent transition-colors group",
                      isRTL ? "text-right" : "text-left"
                    )}
                  >
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <suggestion.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{suggestion.title}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {suggestion.subtitle}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg">
                      {getTypeLabel(suggestion.type)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - directly below search bar */}
        <div className="mt-8 flex justify-center animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/70 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
};